import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDevices } from '../api/queries'
import type { Project } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDate } from '../lib/format'
import { DeviceForm } from './DeviceForm'
import { Card } from './ui'

/**
 * Enhederne der blev installeret på sagen, og som skal serviceres fremover.
 *
 * Når sagen er afsluttet og der endnu ikke er registreret nogen enhed, spørger
 * kortet direkte, om der skal service på. Det er det sted i forløbet, hvor man
 * ved det - bagefter bliver det aldrig gjort (docs/DECISIONS.md beslutning 024).
 *
 * Enhederne hører til kunden og lever videre, efter at sagen er lukket.
 */
export function ProjectDevicesCard({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const { isProjectManager } = useAuth()
  const { data: devices } = useDevices({ projectId: project.id, includeInactive: true })
  const [adding, setAdding] = useState(false)

  const mine = devices ?? []
  const isCompleted = project.status.isCompleted

  // Ingen enheder, sagen kører endnu, og brugeren må ikke rette: der er intet at vise.
  if (mine.length === 0 && !isCompleted && !isProjectManager) {
    return null
  }

  if (adding) {
    return (
      <DeviceForm
        device={null}
        projectId={project.id}
        customerId={project.customerId ?? undefined}
        onClose={() => setAdding(false)}
        // Bliv i formularen-tilstanden lukket, men lad brugeren tilføje flere i træk.
        onSaved={() => setAdding(false)}
      />
    )
  }

  return (
    <Card
      title={`Enheder til service${mine.length ? ` (${mine.length})` : ''}`}
      actions={
        canEdit || isProjectManager ? (
          <div className="row">
            {mine.length > 0 && (
              <Link
                className="btn btn--sm"
                to={`/devices/label?ids=${mine.map((d) => d.id).join(',')}`}
              >
                Print mærkater
              </Link>
            )}
            <button className="btn btn--sm" onClick={() => setAdding(true)}>
              Tilføj enhed
            </button>
          </div>
        ) : undefined
      }
    >
      {mine.length === 0 ? (
        isCompleted ? (
          <div>
            <div className="small" style={{ marginBottom: 6 }}>
              <strong>Skal der service på noget af det, I har installeret?</strong>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Opret en enhed pr. ting der skal serviceres - batteri, tavle, anlæg - og vælg hvor tit.
              Så kommer den frem i serviceoverblikket, og der kan printes et QR-mærkat til at sætte
              på den.
            </p>
          </div>
        ) : (
          <div className="muted small">
            Ingen enheder registreret. De oprettes typisk, når sagen afsluttes.
          </div>
        )
      ) : (
        <div className="stack">
          {mine.map((device) => (
            <Link key={device.id} className="row row--between device-row" to={`/devices/${device.id}`}>
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8 }}>
                  <strong className="small">{device.name}</strong>
                  <span className="tiny muted device-row__code">{device.code}</span>
                </div>
                <div className="tiny muted">{device.location || 'Ingen placering angivet'}</div>
              </div>
              <div className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
                {device.serviceIntervalMonths
                  ? `Næste service ${formatDate(device.nextServiceDate)}`
                  : 'Ingen serviceaftale'}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  )
}
